import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  Plus,
  Search,
  Edit,
  Trash2,
  Sun,
  Zap,
  Home,
  Battery,
  DollarSign,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = {
  panneau_solaire: { label: "Panneau solaire", icon: Sun, color: "text-yellow-600" },
  onduleur: { label: "Onduleur", icon: Zap, color: "text-blue-600" },
  batterie: { label: "Batterie", icon: Battery, color: "text-green-600" },
  structure: { label: "Structure", icon: Package, color: "text-gray-600" },
  coffret: { label: "Coffret", icon: Package, color: "text-purple-600" },
  pompe_chaleur: { label: "Pompe à chaleur", icon: Home, color: "text-cyan-600" },
  chaudiere: { label: "Chaudière", icon: Zap, color: "text-orange-600" },
  chauffe_eau: { label: "Chauffe-eau", icon: Zap, color: "text-red-600" },
  vmc: { label: "VMC", icon: Zap, color: "text-indigo-600" },
  isolation: { label: "Isolation", icon: Home, color: "text-brown-600" },
  fenetre: { label: "Fenêtre", icon: Home, color: "text-blue-400" },
  porte: { label: "Porte", icon: Home, color: "text-gray-500" },
  climatisation: { label: "Climatisation", icon: Zap, color: "text-sky-600" },
  main_oeuvre: { label: "Main d'œuvre", icon: DollarSign, color: "text-green-700" },
  demarches_admin: { label: "Démarches admin", icon: Package, color: "text-slate-600" },
  autre: { label: "Autre", icon: Package, color: "text-gray-400" },
};

const ProduitsPage = ({ user }) => {
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategorie, setFilterCategorie] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProduit, setSelectedProduit] = useState(null);

  const emptyProduit = {
    reference: "",
    nom: "",
    description: "",
    marque: "",
    categorie: "panneau_solaire",
    prix_achat_ht: 0,
    prix_vente_ht: 0,
    taux_tva: 20,
    unite: "unité",
    eligible_cee: false,
    eligible_maprimerenov: false,
    eligible_tva_reduite: false,
    taux_tva_reduite: 5.5,
    garantie_constructeur_annees: 0,
    certifications: [],
    gere_en_stock: false,
    stock_actuel: 0,
    stock_minimum: 0,
    caracteristiques: {},
  };

  const [formData, setFormData] = useState(emptyProduit);

  useEffect(() => {
    fetchProduits();
  }, []);

  const fetchProduits = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/produits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProduits(response.data);
    } catch (error) {
      console.error("Erreur chargement produits:", error);
      toast.error("Erreur lors du chargement des produits");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      
      if (editMode) {
        await axios.put(`${API}/produits/${selectedProduit.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Produit mis à jour !");
      } else {
        await axios.post(`${API}/produits`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Produit créé !");
      }
      
      setShowDialog(false);
      fetchProduits();
      resetForm();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (produit) => {
    setSelectedProduit(produit);
    setFormData(produit);
    setEditMode(true);
    setShowDialog(true);
  };

  const handleDelete = async (produitId) => {
    if (!confirm("Confirmer la suppression ?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/produits/${produitId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Produit supprimé !");
      fetchProduits();
    } catch (error) {
      console.error("Erreur suppression:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setFormData(emptyProduit);
    setEditMode(false);
    setSelectedProduit(null);
  };

  const filteredProduits = produits.filter((produit) => {
    const matchSearch =
      produit.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      produit.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      produit.marque?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchCategorie = filterCategorie === "all" || produit.categorie === filterCategorie;
    
    return matchSearch && matchCategorie;
  });

  const stats = {
    total: produits.length,
    categories: [...new Set(produits.map(p => p.categorie))].length,
    eligible_aides: produits.filter(p => p.eligible_cee || p.eligible_maprimerenov).length,
    valeur_stock: produits.reduce((sum, p) => sum + (p.prix_vente_ht * (p.stock_actuel || 0)), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Package className="w-8 h-8 text-red-600" />
            Catalogue Produits
          </h1>
          <p className="text-slate-600 mt-1">
            Gérez votre catalogue d'équipements et prestations
          </p>
        </div>
        {user?.role === "admin" && (
          <Button
            onClick={() => {
              resetForm();
              setShowDialog(true);
            }}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau produit
          </Button>
        )}
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
            <p className="text-sm text-slate-600">Produits</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">{stats.categories}</div>
            <p className="text-sm text-slate-600">Catégories</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{stats.eligible_aides}</div>
            <p className="text-sm text-slate-600">Éligibles aides</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">
              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(stats.valeur_stock)}
            </div>
            <p className="text-sm text-slate-600">Valeur stock</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterCategorie}
              onChange={(e) => setFilterCategorie(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-red-500"
            >
              <option value="all">Toutes les catégories</option>
              {Object.entries(CATEGORIES).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Liste des produits */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProduits.map((produit) => {
          const CategorieConfig = CATEGORIES[produit.categorie] || CATEGORIES.autre;
          
          return (
            <Card key={produit.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <CategorieConfig.icon className={`w-5 h-5 ${CategorieConfig.color}`} />
                    <Badge variant="outline">{CategorieConfig.label}</Badge>
                  </div>
                  {user?.role === "admin" && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(produit)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(produit.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <h3 className="font-semibold text-slate-800 mb-1">{produit.nom}</h3>
                <p className="text-sm text-slate-600 mb-2">Réf: {produit.reference}</p>
                {produit.marque && (
                  <p className="text-sm text-slate-500 mb-3">{produit.marque}</p>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Prix vente HT:</span>
                    <span className="font-semibold text-red-600">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(produit.prix_vente_ht)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">TVA:</span>
                    <span className="font-medium">{produit.taux_tva}%</span>
                  </div>
                  {produit.gere_en_stock && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Stock:</span>
                      <span className={`font-medium ${produit.stock_actuel <= produit.stock_minimum ? 'text-red-600' : 'text-green-600'}`}>
                        {produit.stock_actuel} {produit.unite}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-1">
                  {produit.eligible_cee && (
                    <Badge className="bg-blue-100 text-blue-800 text-xs">CEE</Badge>
                  )}
                  {produit.eligible_maprimerenov && (
                    <Badge className="bg-green-100 text-green-800 text-xs">MaPrimeRénov</Badge>
                  )}
                  {produit.eligible_tva_reduite && (
                    <Badge className="bg-purple-100 text-purple-800 text-xs">TVA réduite</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {filteredProduits.length === 0 && (
          <div className="col-span-full">
            <Card>
              <CardContent className="pt-6 text-center text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aucun produit trouvé</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Dialog Création/Edition */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? "Modifier le produit" : "Créer un nouveau produit"}</DialogTitle>
            <DialogDescription>
              Remplissez les informations du produit
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Référence *</Label>
                <Input
                  value={formData.reference}
                  onChange={(e) => setFormData({...formData, reference: e.target.value})}
                  placeholder="REF-001"
                />
              </div>
              <div>
                <Label>Catégorie *</Label>
                <select
                  value={formData.categorie}
                  onChange={(e) => setFormData({...formData, categorie: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                >
                  {Object.entries(CATEGORIES).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Nom du produit *</Label>
              <Input
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
                placeholder="Ex: Panneau photovoltaïque 500Wc"
              />
            </div>

            <div>
              <Label>Marque</Label>
              <Input
                value={formData.marque}
                onChange={(e) => setFormData({...formData, marque: e.target.value})}
                placeholder="Dualsun, Atlantic..."
              />
            </div>

            <div>
              <Label>Description</Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Prix achat HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.prix_achat_ht}
                  onChange={(e) => setFormData({...formData, prix_achat_ht: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Prix vente HT (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.prix_vente_ht}
                  onChange={(e) => setFormData({...formData, prix_vente_ht: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Unité</Label>
                <Input
                  value={formData.unite}
                  onChange={(e) => setFormData({...formData, unite: e.target.value})}
                  placeholder="unité, m², ml"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <Label>Taux TVA normal (%)</Label>
                <select
                  value={formData.taux_tva}
                  onChange={(e) => setFormData({...formData, taux_tva: parseFloat(e.target.value)})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                >
                  <option value={20}>20%</option>
                  <option value={10}>10%</option>
                  <option value={5.5}>5.5%</option>
                </select>
              </div>
              <div>
                <Label>Garantie constructeur (années)</Label>
                <Input
                  type="number"
                  value={formData.garantie_constructeur_annees}
                  onChange={(e) => setFormData({...formData, garantie_constructeur_annees: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.eligible_cee}
                  onChange={(e) => setFormData({...formData, eligible_cee: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label>Éligible aux aides CEE</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.eligible_maprimerenov}
                  onChange={(e) => setFormData({...formData, eligible_maprimerenov: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label>Éligible MaPrimeRénov</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.eligible_tva_reduite}
                  onChange={(e) => setFormData({...formData, eligible_tva_reduite: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label>Éligible TVA réduite (5.5% ou 10%)</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.gere_en_stock}
                  onChange={(e) => setFormData({...formData, gere_en_stock: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label>Gérer en stock</Label>
              </div>
            </div>

            {formData.gere_en_stock && (
              <div className="grid grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
                <div>
                  <Label>Stock actuel</Label>
                  <Input
                    type="number"
                    value={formData.stock_actuel}
                    onChange={(e) => setFormData({...formData, stock_actuel: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Stock minimum</Label>
                  <Input
                    type="number"
                    value={formData.stock_minimum}
                    onChange={(e) => setFormData({...formData, stock_minimum: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Stock maximum</Label>
                  <Input
                    type="number"
                    value={formData.stock_maximum}
                    onChange={(e) => setFormData({...formData, stock_maximum: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  resetForm();
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSubmit}
                className="bg-red-600 hover:bg-red-700"
                disabled={!formData.reference || !formData.nom}
              >
                {editMode ? "Mettre à jour" : "Créer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProduitsPage;
